import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import Constants from 'Constants'
import { getSelectedBusinessId } from 'data/select-account/selectAccount'
import { getToken } from 'providers/firebaseAuth'
import { REAUTH_OUTCOME, recoverFromUnauthorized } from 'providers/reauth'
import { STATUS_CODE } from 'settings/status'

import { CACHE_TAG_TYPES } from './cacheTagTypes'

import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'

const { SERVER_URL, BUSINESS_IDENTIFIER } = Constants
const { UNAUTHORIZED } = STATUS_CODE

// Seconds an unused cache entry is kept before the next request refetches it.
export const DEFAULT_CACHE_SUBSCRIPTION_DURATION = 60

// TODO: restore a request timeout — fetchBaseQuery only gained `timeout` in RTK 1.9; this is 1.8.3.
const rawBaseQuery = fetchBaseQuery({
  baseUrl: SERVER_URL,
  prepareHeaders: async (headers, { getState }) => {
    const token = await getToken()
    const state = getState()
    const businessId = getSelectedBusinessId(state)
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
      if (businessId) {
        headers.set(BUSINESS_IDENTIFIER, businessId)
      }
    }

    return headers
  },
})

// A 401 that survived refresh-and-retry; rtkQueryErrorLogger reads it and owns the sign-out.
export type ReauthFetchError = FetchBaseQueryError & { sessionExpired?: boolean }

type ReauthResult = Awaited<ReturnType<typeof rawBaseQuery>>

// Flagged, not signed out: importing handleFirebaseLogout here pulls the store in and breaks api's own load.
const asExpiredSession = (result: ReauthResult): ReauthResult =>
  ({ ...result, error: { ...result.error, sessionExpired: true } } as unknown as ReauthResult)

// Nothing ran behind a 401, so the request is safe to repeat; only a second one proves the session is over.
// eslint-disable-next-line @typescript-eslint/ban-types -- RTK's own extra-options default; the meta type is restated or no endpoint can read a status code
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, ReauthFetchError, {}, FetchBaseQueryMeta> = async (
  args,
  apiContext,
  extraOptions
) => {
  const result = await rawBaseQuery(args, apiContext, extraOptions)
  if (result.error?.status !== UNAUTHORIZED) {
    return result
  }

  const outcome = await recoverFromUnauthorized()
  if (outcome === REAUTH_OUTCOME.SESSION_OVER) {
    return asExpiredSession(result)
  }

  if (outcome !== REAUTH_OUTCOME.REFRESHED) {
    return result
  }

  const retried = await rawBaseQuery(args, apiContext, extraOptions)
  return retried.error?.status === UNAUTHORIZED ? asExpiredSession(retried) : retried
}

const api = createApi({
  reducerPath: 'apiReducer',
  tagTypes: CACHE_TAG_TYPES,
  baseQuery: baseQueryWithReauth,

  // how many seconds the data will be cached between requests
  keepUnusedDataFor: DEFAULT_CACHE_SUBSCRIPTION_DURATION,
  // RTK 2's 'delayed' holds every refetch while any request is in flight; refusedRefetchRetry covers a lane mid-walk instead.
  invalidationBehavior: 'immediately',
  endpoints: () => ({}),
})

export default api
